import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowUpDown,
  Building2,
  CheckCircle2,
  CreditCard,
  Edit2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LogOut,
  Plus,
  Power,
  QrCode,
  ShieldCheck,
  Tag,
  Trash2,
  Upload,
  WalletCards,
  Zap,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const financeApi = axios.create({
  baseURL: BACKEND_URL,
  headers: { 'Content-Type': 'application/json' },
});

financeApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('finance_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const slugify = (value) => (value || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const visibleUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const normalizedFlow = (method) => String(method?.flow || 'both').toLowerCase().replace(/[^a-z]/g, '');

const methodSupportsDeposit = (method) => {
  const flow = normalizedFlow(method);
  return ['both', 'deposit', 'cashin', 'payin', 'load'].includes(flow);
};

const methodSupportsWithdraw = (method) => {
  const flow = normalizedFlow(method);
  return ['both', 'withdraw', 'withdrawal', 'cashout', 'redeem'].includes(flow);
};

const badgeToneClasses = {
  gold: 'border-[#D4AF37]/50 bg-[#D4AF37]/15 text-[#F5D76E]',
  green: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-300',
  blue: 'border-sky-400/40 bg-sky-500/15 text-sky-300',
  purple: 'border-purple-400/40 bg-purple-500/15 text-purple-300',
  red: 'border-rose-400/40 bg-rose-500/15 text-rose-300',
};

const EMPTY_FORM = {
  payment_mode: 'LOCAL_MANUAL',
  title: '',
  flow: 'deposit',
  method_details: '',
  min_amount: 10,
  max_amount: 5000,
  receiving_address: '',
  tags: '',
  approval_bot_type: 'auto',
  admin_note: '',
  logo_url: '',
  enabled: true,
  display_order: 10,
  player_badge_label: '',
  player_badge_tone: 'gold',
  provider_code: 'oneclickpay',
  gateway_method_code: 'cashapp',
  gateway_integration_id: '',
  base_url: 'https://checkout.1clickpay.vip',
  api_key: '',
  api_secret: '',
  group_id: '',
  group_name: '',
  payment_path: 'website',
};

function LoginCard({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await financeApi.post('/api/finance/auth/login', form);
      const token = res.data?.token;
      if (!token) throw new Error('Missing finance token');
      localStorage.setItem('finance_token', token);
      toast.success('Finance portal unlocked');
      onLogin(token, res.data?.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Finance login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-[#D4AF37]/30 bg-[#131822] p-6 shadow-2xl shadow-black/40"
        data-testid="finance-login-form"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-[#F5D76E]" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">Finance Staff</h1>
            <p className="text-sm text-gray-400">Tenant wallet and payment API portal</p>
          </div>
        </div>
        <div className="space-y-3">
          <Input
            type="email"
            placeholder="finance@example.com"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            className="bg-[#0B0E14] border-white/10 text-white"
            required
          />
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              className="bg-[#0B0E14] border-white/10 text-white pr-11"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="mt-5 w-full bg-[#D4AF37] text-black hover:bg-[#F5D76E] rounded-xl">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
          Login to Finance
        </Button>
      </form>
    </div>
  );
}

export default function FinancePortalPage() {
  const [token, setToken] = useState(() => localStorage.getItem('finance_token') || '');
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [methods, setMethods] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadingQr, setUploadingQr] = useState('');
  const qrInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => (tenant.id || '') === selectedTenantId) || tenants[0],
    [tenants, selectedTenantId],
  );

  const loadMeAndTenants = async () => {
    const [meRes, tenantRes] = await Promise.all([
      financeApi.get('/api/finance/auth/me'),
      financeApi.get('/api/finance/tenants'),
    ]);
    setUser(meRes.data?.user || null);
    const nextTenants = tenantRes.data?.tenants || [];
    setTenants(nextTenants);
    if (!selectedTenantId && nextTenants.length > 0) {
      const firstTenant = nextTenants.find((tenant) => !tenant.is_main) || nextTenants[0];
      setSelectedTenantId(firstTenant.id || '');
    }
  };

  const loadTenantPayments = async (tenantId = selectedTenantId, options = {}) => {
    const quiet = !!options.quiet;
    if (!quiet) setLoading(true);
    try {
      const params = { tenant_id: tenantId || '', _ts: Date.now() };
      const [methodRes, gatewayRes] = await Promise.all([
        financeApi.get('/api/finance/payment-methods', { params }),
        financeApi.get('/api/finance/payment-gateways', { params }),
      ]);
      setMethods(methodRes.data?.methods || []);
      setGateways(gatewayRes.data?.gateways || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tenant payments');
      if (err.response?.status === 401) {
        localStorage.removeItem('finance_token');
        setToken('');
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadMeAndTenants().catch((err) => {
      toast.error(err.response?.data?.detail || 'Finance session expired');
      localStorage.removeItem('finance_token');
      setToken('');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token && tenants.length > 0) loadTenantPayments(selectedTenantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedTenantId, tenants.length]);

  useEffect(() => {
    if (!token || tenants.length === 0) return undefined;
    const id = window.setInterval(() => {
      loadTenantPayments(selectedTenantId, { quiet: true });
    }, 3000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedTenantId, tenants.length]);

  const gatewayFor = (method) => gateways.find((gateway) => gateway.provider_code === (method.gateway_provider || method.provider_code));

  const logout = async () => {
    await financeApi.post('/api/finance/auth/logout').catch(() => {});
    localStorage.removeItem('finance_token');
    setToken('');
    setUser(null);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, tenant_id: selectedTenantId });
    setShowForm(true);
  };

  const openEdit = (method) => {
    const isGateway = method.payment_mode === 'GATEWAY';
    const gateway = gatewayFor(method);
    setEditing(method);
    setForm({
      ...EMPTY_FORM,
      tenant_id: method.tenant_id || selectedTenantId,
      payment_mode: isGateway ? 'GATEWAY' : 'LOCAL_MANUAL',
      title: method.title || method.name || '',
      flow: method.flow || 'both',
      method_details: method.method_details || method.instructions || '',
      min_amount: method.min_amount ?? 10,
      max_amount: method.max_amount ?? 5000,
      receiving_address: method.receiving_address || '',
      tags: method.tags || '',
      approval_bot_type: method.approval_bot_type || 'auto',
      admin_note: method.admin_note || '',
      logo_url: method.logo_url || '',
      enabled: method.enabled !== false,
      display_order: method.display_order ?? 10,
      player_badge_label: method.player_badge_label || '',
      player_badge_tone: method.player_badge_tone || 'gold',
      provider_code: method.gateway_provider || gateway?.provider_code || 'oneclickpay',
      gateway_method_code: method.gateway_method_code || method.gateway_method || method.method || 'cashapp',
      gateway_integration_id: gateway?.id || '',
      base_url: gateway?.base_url || gateway?.config?.base_url || 'https://checkout.1clickpay.vip',
      api_key: '',
      api_secret: '',
      group_id: gateway?.config?.group_id || '',
      group_name: gateway?.config?.group_name || '',
      payment_path: gateway?.config?.payment_path || 'website',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upload_type', 'payment_logo');
      const res = await financeApi.post('/api/finance/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((p) => ({ ...p, logo_url: res.data?.url || res.data?.public_url || '' }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const triggerQrUpload = (methodId) => {
    if (!qrInputRef.current) return;
    qrInputRef.current.dataset.methodId = methodId;
    qrInputRef.current.click();
  };

  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    const methodId = e.target.dataset.methodId;
    if (!file || !methodId) return;
    const fd = new FormData();
    fd.append('qr_image', file);
    setUploadingQr(methodId);
    try {
      await financeApi.post(`/api/finance/payment-methods/${methodId}/qr`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('QR uploaded');
      loadTenantPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'QR upload failed');
    } finally {
      setUploadingQr('');
      e.target.value = '';
    }
  };

  const savePayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isGateway = form.payment_mode === 'GATEWAY';
      let providerCode = form.provider_code || 'oneclickpay';
      if (isGateway) {
        const gatewayPayload = {
          tenant_id: selectedTenantId || '',
          provider_code: providerCode,
          display_name: providerCode === 'oneclickpay' ? '1ClickPay Website API' : form.title,
          base_url: form.base_url || undefined,
          group_id: form.group_id || undefined,
          group_name: form.group_name || undefined,
          payment_path: form.payment_path || 'website',
          enabled: form.enabled,
        };
        if (form.api_key) gatewayPayload.api_key = form.api_key;
        if (form.api_secret) gatewayPayload.webhook_secret = form.api_secret;

        if (editing && form.gateway_integration_id) {
          await financeApi.put(`/api/finance/payment-gateways/${form.gateway_integration_id}`, gatewayPayload);
        } else {
          const res = await financeApi.post('/api/finance/payment-gateways', gatewayPayload);
          providerCode = res.data?.gateway?.provider_code || providerCode;
        }
      }

      const methodCode = isGateway ? (form.gateway_method_code || slugify(form.title)) : slugify(form.title);
      const methodPayload = {
        tenant_id: selectedTenantId || '',
        payment_mode: form.payment_mode,
        title: form.title,
        name: form.title,
        method: methodCode,
        flow: form.flow,
        visibility: 'both',
        method_details: form.method_details || (isGateway ? `Checkout via ${form.title}` : `Pay via ${form.title}`),
        receiving_address: isGateway ? '' : form.receiving_address,
        tags: isGateway ? '' : form.tags,
        approval_bot_type: isGateway ? 'auto' : form.approval_bot_type,
        admin_note: form.admin_note,
        logo_url: form.logo_url,
        min_amount: Number(form.min_amount) || 0,
        max_amount: Number(form.max_amount) || 0,
        enabled: !!form.enabled,
        display_order: Number(form.display_order) || 0,
        player_badge_label: form.player_badge_label || '',
        player_badge_tone: form.player_badge_tone || 'gold',
      };
      if (isGateway) {
        methodPayload.gateway_provider = providerCode;
        methodPayload.gateway_method = methodCode;
        methodPayload.gateway_method_code = methodCode;
      }

      if (editing) {
        await financeApi.put(`/api/finance/payment-methods/${editing.id}`, methodPayload);
        toast.success('Payment updated');
      } else {
        await financeApi.post('/api/finance/payment-methods', methodPayload);
        toast.success('Payment added');
      }
      closeForm();
      loadTenantPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleMethod = async (method) => {
    try {
      await financeApi.post(`/api/finance/payment-methods/${method.id}/toggle`);
      loadTenantPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const deleteMethod = async (method) => {
    if (!window.confirm(`Delete ${method.title || method.name}?`)) return;
    try {
      await financeApi.delete(`/api/finance/payment-methods/${method.id}`);
      toast.success('Deleted');
      loadTenantPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const renderMethodCard = (method) => {
    const isGateway = method.payment_mode === 'GATEWAY';
    const gateway = gatewayFor(method);
    const qrUrl = method.qr_code_url || method.qr_image_url;
    return (
      <div key={method.id} className={`rounded-2xl border ${isGateway ? 'border-purple-500/25' : 'border-[#D4AF37]/20'} bg-[#111824] overflow-hidden`} data-testid={`finance-method-${method.id}`}>
        <div className="p-4 border-b border-white/5 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {method.logo_url ? (
              <img src={visibleUrl(method.logo_url)} alt={method.title} className="h-11 w-11 rounded-xl object-cover bg-white" />
            ) : (
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${isGateway ? 'bg-purple-500/15' : 'bg-[#D4AF37]/15'}`}>
                {isGateway ? <Zap className="h-5 w-5 text-purple-300" /> : <CreditCard className="h-5 w-5 text-[#F5D76E]" />}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-white truncate">{method.title || method.name}</h3>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] ${isGateway ? 'border-purple-500/30 bg-purple-500/15 text-purple-200' : 'border-sky-500/30 bg-sky-500/15 text-sky-200'}`}>
                  {isGateway ? 'API' : 'MANUAL'}
                </span>
              </div>
              <p className="text-xs text-gray-400 truncate">{isGateway ? (gateway?.name || method.gateway_provider || 'Gateway') : (method.tags || method.receiving_address || method.method_details || 'Manual wallet')}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`rounded-full px-2 py-0.5 text-[10px] ${method.enabled !== false ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
              {method.enabled !== false ? 'Active' : 'Disabled'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-gray-300">
              <ArrowUpDown className="h-3 w-3 text-[#D4AF37]" /> Order {method.display_order ?? 0}
            </span>
            {method.player_badge_label && (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${badgeToneClasses[method.player_badge_tone] || badgeToneClasses.gold}`}>
                <Tag className="h-3 w-3" /> {method.player_badge_label}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-white/5 border-b border-white/5 bg-[#0B0E14]/70">
          <div className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Min</div>
            <div className="text-sm font-bold text-white">${Number(method.min_amount || 0).toLocaleString()}</div>
          </div>
          <div className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Max</div>
            <div className="text-sm font-bold text-white">${Number(method.max_amount || 0).toLocaleString()}</div>
          </div>
        </div>
        {!isGateway && (
          <div className="p-4 space-y-3">
            {qrUrl ? (
              <img src={visibleUrl(qrUrl)} alt={`${method.title} QR`} className="h-28 w-full rounded-xl bg-white object-contain" />
            ) : (
              <div className="h-20 rounded-xl border border-dashed border-white/10 bg-black/20 flex items-center justify-center text-xs text-gray-500">
                No QR/payment image
              </div>
            )}
            <Button onClick={() => triggerQrUpload(method.id)} disabled={uploadingQr === method.id} variant="outline" className="w-full border-white/10 rounded-xl">
              {uploadingQr === method.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Upload QR / Payment Image
            </Button>
          </div>
        )}
        <div className="p-4 flex items-center gap-2">
          <Button onClick={() => openEdit(method)} variant="outline" className="flex-1 rounded-full border-white/10">
            <Edit2 className="mr-2 h-4 w-4" /> Edit
          </Button>
          <button onClick={() => toggleMethod(method)} className={`${method.enabled !== false ? 'text-emerald-300' : 'text-gray-400'} rounded-xl p-2 hover:bg-white/5`}>
            <Power className="h-4 w-4" />
          </button>
          <button onClick={() => deleteMethod(method)} className="rounded-xl p-2 text-rose-300 hover:bg-white/5">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderSection = (title, subtitle, items, tone) => (
    <section className="space-y-4">
      <div className={`rounded-2xl border ${tone === 'red' ? 'border-orange-500/20 bg-orange-500/5' : 'border-sky-500/20 bg-sky-500/5'} px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">{title}</h2>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300">{items.length} methods</span>
        </div>
      </div>
      {items.length ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[1900px]:grid-cols-5">
          {items.map(renderMethodCard)}
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-[#111824] py-10 text-center text-sm text-gray-500">
          No {title.toLowerCase()} methods yet.
        </div>
      )}
    </section>
  );

  if (!token) {
    return <LoginCard onLogin={(nextToken, nextUser) => { setToken(nextToken); setUser(nextUser || null); }} />;
  }

  const cashInMethods = methods.filter(methodSupportsDeposit);
  const withdrawMethods = methods.filter(methodSupportsWithdraw);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="finance-portal-page">
      <input ref={qrInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleQrUpload} />
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0B0E14]/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-none items-center justify-between gap-4 px-6 py-4 2xl:px-10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/15 flex items-center justify-center">
              <WalletCards className="h-5 w-5 text-[#F5D76E]" />
            </div>
            <div>
              <h1 className="text-xl font-black">Finance Portal</h1>
              <p className="text-xs text-gray-400">Logged in as {user?.name || user?.email || 'finance staff'}</p>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full border-white/10">
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-none grid-cols-1 gap-5 px-6 py-6 lg:grid-cols-[280px_minmax(0,1fr)] 2xl:px-10">
        <aside className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#111824] p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
              <Building2 className="h-4 w-4 text-[#D4AF37]" /> Tenant List
            </div>
            <div className="space-y-2 max-h-[68vh] overflow-y-auto pr-1">
              {tenants.map((tenant) => {
                const id = tenant.id || '';
                const active = id === selectedTenantId;
                return (
                  <button
                    key={`${tenant.slug}-${id}`}
                    onClick={() => setSelectedTenantId(id)}
                    className={`w-full rounded-xl border px-3 py-3 text-left transition ${active ? 'border-[#D4AF37]/50 bg-[#D4AF37]/10' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'}`}
                    data-testid={`finance-tenant-${tenant.slug}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-white truncate">{tenant.name || tenant.slug || 'Tenant'}</span>
                      {active && <CheckCircle2 className="h-4 w-4 text-[#D4AF37]" />}
                    </div>
                    <div className="text-xs text-gray-500">@{tenant.slug || 'main'}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <section className="space-y-6">
          <div className="rounded-2xl border border-white/10 bg-[#111824] p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Selected tenant</p>
                <h2 className="text-2xl font-black">{selectedTenant?.name || 'Tenant'}</h2>
                <p className="text-sm text-gray-400">
                  Finance staff can configure this tenant's deposit wallets, cashout wallets, and API checkout methods.
                </p>
              </div>
              <Button onClick={openAdd} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#F5D76E]" data-testid="finance-add-payment">
                <Plus className="mr-2 h-4 w-4" /> Add Payment
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex h-60 items-center justify-center rounded-2xl border border-white/10 bg-[#111824]">
              <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
            </div>
          ) : (
            <>
              {renderSection('Cash In Dashboard', 'Deposit methods shown to players for wallet top-ups.', cashInMethods, 'sky')}
              {renderSection('Withdrawal Dashboard', 'Cashout methods players can select for withdrawals.', withdrawMethods, 'red')}
            </>
          )}
        </section>
      </main>

      <Modal open={showForm} onClose={closeForm} title={editing ? 'Edit Tenant Payment' : 'Add Tenant Payment'} maxWidth="max-w-2xl">
        <form onSubmit={savePayment} className="space-y-4" data-testid="finance-payment-form">
          <div className="grid grid-cols-2 gap-2 rounded-xl border border-white/10 bg-[#0B0E14] p-1">
            <button
              type="button"
              onClick={() => setForm((p) => ({ ...p, payment_mode: 'LOCAL_MANUAL' }))}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${form.payment_mode === 'LOCAL_MANUAL' ? 'bg-sky-500/20 text-sky-100' : 'text-gray-400 hover:text-white'}`}
            >
              <QrCode className="mr-2 inline h-4 w-4" /> Manual / QR
            </button>
            <button
              type="button"
              onClick={() => setForm((p) => ({ ...p, payment_mode: 'GATEWAY', flow: 'deposit' }))}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${form.payment_mode === 'GATEWAY' ? 'bg-purple-500/20 text-purple-100' : 'text-gray-400 hover:text-white'}`}
            >
              <Zap className="mr-2 inline h-4 w-4" /> API Gateway
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs text-gray-400">Display Name *</span>
              <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} required className="bg-[#0B0E14] border-white/10" placeholder="Cash App, Apple Pay, Chime" />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-gray-400">Flow</span>
              <select value={form.flow} onChange={(e) => setForm((p) => ({ ...p, flow: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2.5 text-sm text-white">
                <option value="deposit">Deposit Only</option>
                <option value="withdraw">Withdrawal Only</option>
                <option value="both">Both</option>
              </select>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs text-gray-400">Min Amount</span>
              <Input type="number" value={form.min_amount} onChange={(e) => setForm((p) => ({ ...p, min_amount: e.target.value }))} className="bg-[#0B0E14] border-white/10" />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-gray-400">Max Amount</span>
              <Input type="number" value={form.max_amount} onChange={(e) => setForm((p) => ({ ...p, max_amount: e.target.value }))} className="bg-[#0B0E14] border-white/10" />
            </label>
          </div>

          <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#F5D76E]">
              <ArrowUpDown className="h-4 w-4" /> Player card display
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="space-y-1">
                <span className="text-xs text-gray-400">Player Order</span>
                <Input type="number" value={form.display_order} onChange={(e) => setForm((p) => ({ ...p, display_order: e.target.value }))} className="bg-[#0B0E14] border-white/10" />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-gray-400">Card Tag</span>
                <Input value={form.player_badge_label} onChange={(e) => setForm((p) => ({ ...p, player_badge_label: e.target.value }))} className="bg-[#0B0E14] border-white/10" placeholder="HOT, preferred, +10%" maxLength={22} />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-gray-400">Tag Color</span>
                <select value={form.player_badge_tone} onChange={(e) => setForm((p) => ({ ...p, player_badge_tone: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2.5 text-sm text-white">
                  <option value="gold">Gold</option>
                  <option value="green">Green</option>
                  <option value="blue">Blue</option>
                  <option value="purple">Purple</option>
                  <option value="red">Red</option>
                </select>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <span className="text-xs text-gray-400">Payment Logo</span>
            <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleLogoUpload} />
            <div className="flex items-center gap-3">
              {form.logo_url && <img src={visibleUrl(form.logo_url)} alt="Payment logo" className="h-12 w-12 rounded-xl object-cover bg-white" />}
              <Button type="button" variant="outline" className="rounded-full border-white/10" onClick={() => logoInputRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4" /> {form.logo_url ? 'Change Logo' : 'Upload Logo'}
              </Button>
              {form.logo_url && (
                <button type="button" onClick={() => setForm((p) => ({ ...p, logo_url: '' }))} className="text-xs text-rose-300">
                  Remove
                </button>
              )}
            </div>
          </div>

          {form.payment_mode === 'GATEWAY' ? (
            <div className="space-y-3 rounded-xl border border-purple-500/20 bg-purple-500/5 p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-xs text-gray-400">Provider</span>
                  <select value={form.provider_code} onChange={(e) => setForm((p) => ({ ...p, provider_code: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2.5 text-sm text-white">
                    <option value="oneclickpay">1ClickPay Website API</option>
                    <option value="nowpayments">NOWPayments Crypto</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-gray-400">Payment Method</span>
                  <select value={form.gateway_method_code} onChange={(e) => setForm((p) => ({ ...p, gateway_method_code: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2.5 text-sm text-white">
                    <option value="cashapp">Cash App</option>
                    <option value="applepay">Apple Pay</option>
                    <option value="googlepay">Google Pay</option>
                    <option value="crypto">Crypto</option>
                  </select>
                </label>
              </div>
              <label className="space-y-1 block">
                <span className="text-xs text-gray-400">API Base URL</span>
                <Input value={form.base_url} onChange={(e) => setForm((p) => ({ ...p, base_url: e.target.value }))} className="bg-[#0B0E14] border-white/10 font-mono" />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-xs text-gray-400">API Key {editing && '(blank keeps current)'}</span>
                  <Input type="password" value={form.api_key} onChange={(e) => setForm((p) => ({ ...p, api_key: e.target.value }))} className="bg-[#0B0E14] border-white/10 font-mono" placeholder={editing ? 'Leave blank to keep' : 'x-api-key'} />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-gray-400">Webhook/API Secret {editing && '(optional)'}</span>
                  <Input type="password" value={form.api_secret} onChange={(e) => setForm((p) => ({ ...p, api_secret: e.target.value }))} className="bg-[#0B0E14] border-white/10 font-mono" placeholder="webhook secret if provider uses one" />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-3 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3">
                <label className="space-y-1">
                  <span className="text-xs text-gray-400">Gateway Group ID</span>
                  <Input value={form.group_id} onChange={(e) => setForm((p) => ({ ...p, group_id: e.target.value }))} className="bg-[#0B0E14] border-white/10 font-mono" placeholder="starlight" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-gray-400">Gateway Group Name</span>
                  <Input value={form.group_name} onChange={(e) => setForm((p) => ({ ...p, group_name: e.target.value }))} className="bg-[#0B0E14] border-white/10" placeholder="Starlight" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-gray-400">Payment Path</span>
                  <Input value={form.payment_path} onChange={(e) => setForm((p) => ({ ...p, payment_path: e.target.value }))} className="bg-[#0B0E14] border-white/10 font-mono" placeholder="website" />
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-3 rounded-xl border border-sky-500/20 bg-sky-500/5 p-3">
              <label className="space-y-1 block">
                <span className="text-xs text-gray-400">Receiving Wallet / Address</span>
                <Input value={form.receiving_address} onChange={(e) => setForm((p) => ({ ...p, receiving_address: e.target.value }))} className="bg-[#0B0E14] border-white/10 font-mono" />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-gray-400">Tag / Handle / Username</span>
                <Input value={form.tags} onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))} className="bg-[#0B0E14] border-white/10 font-mono" />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-gray-400">Deposit Approval Bot Route</span>
                <select value={form.approval_bot_type} onChange={(e) => setForm((p) => ({ ...p, approval_bot_type: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2.5 text-sm text-white">
                  <option value="auto">Auto detect from payment name</option>
                  <option value="cash_in">Cash In Group</option>
                  <option value="chime_in">Chime In Group</option>
                </select>
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-gray-400">Instructions shown to player</span>
                <Input value={form.method_details} onChange={(e) => setForm((p) => ({ ...p, method_details: e.target.value }))} className="bg-[#0B0E14] border-white/10" />
              </label>
            </div>
          )}

          <label className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0B0E14] px-3 py-2">
            <span className="text-sm text-white">Enabled and visible to players</span>
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))} className="h-5 w-5 accent-[#D4AF37]" />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" className="rounded-full border-white/10" onClick={closeForm}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#F5D76E]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Save Payment
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
